import * as Tone from 'tone';
import { createEventBus } from './bus.js';
import { createMixer } from './mixer.js';
import { createScheduler } from './scheduler.js';
import { createPatternBank, createPattern } from './pattern.js';
import { createHistory } from './history.js';
import { createSong } from './song.js';
import { createAudioClipManager } from './audio-clips.js';

// AppContext is the central wiring hub. Modules register themselves against
// it; UI components read from it. Keeping it as a plain factory (no class)
// makes it trivial to expose to the console for debugging — see main.js.

export function createContext() {
    const bus = createEventBus();
    const mixer = createMixer(bus);
    const patterns = createPatternBank(bus);
    const scheduler = createScheduler(bus);
    const song = createSong(bus);
    bus.on('pattern:removed', (id) => song.pruneRemoved(id));

    const instruments = new Map();   // id → { id, name, kind, channelId, play(...) }
    const lanes = [];                // ordered list of channel-rack lane descriptors
    const modules = [];              // registered feature modules
    let started = false;

    const ctx = {
        Tone,
        bus,
        mixer,
        patterns,
        scheduler,
        song,
        history: null, // assigned below once ctx exists
        audioClips: null, // assigned below once ctx exists

        instruments: {
            register(inst) {
                if (instruments.has(inst.id)) throw new Error(`instrument "${inst.id}" exists`);
                instruments.set(inst.id, inst);
                bus.emit('instrument:added', inst);
            },
            get: (id) => instruments.get(id),
            all: () => Array.from(instruments.values()),
        },

        lanes: {
            add(lane) { lanes.push(lane); bus.emit('lane:added', lane); },
            all: () => lanes.slice(),
            get: (id) => lanes.find(l => l.id === id),
            setName(id, name) {
                const lane = lanes.find(l => l.id === id);
                if (!lane) return;
                lane.name = name;
                bus.emit('lane:changed', { id, field: 'name' });
            },
            setColor(id, color) {
                const lane = lanes.find(l => l.id === id);
                if (!lane) return;
                lane.color = color;
                bus.emit('lane:changed', { id, field: 'color' });
            },
        },

        // Modules register before start(); their register(ctx) runs once the
        // audio context is unlocked.
        addModule(mod) { modules.push(mod); },

        listModules: () => modules.slice(),

        async start() {
            if (started) return;
            started = true;
            try {
                await Tone.start();
                mixer.init();
                if (patterns.list().length === 0) patterns.add(createPattern());
                for (const m of modules) m.register(ctx);
                bus.emit('app:started');
            } catch (e) {
                started = false;
                throw e;
            }
        },

        isStarted: () => started,
    };

    ctx.history = createHistory(ctx);
    ctx.audioClips = createAudioClipManager(ctx);

    return ctx;
}
